import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import { Avatar, Card, CardHeader, Divider, IconButton, Typography } from '@mui/material'
import { Box, Stack, useTheme } from '@mui/system'
import { useEffect, useState } from 'react'
import { ChildStep, ChildStepProps } from './ChildStepComponent'
import { useTranslation } from 'react-i18next'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'


export type StepProps = {
  // The title of the step
  title: string
  // The description of the step
  description: string
  // The icon of the step
  icon: React.ReactNode
  // The optional video link of the step
  videoLink?: string
  // The optional full tutorial link of the step
  fullTutorialLink?: string
  // The list of child steps
  childSteps: Array<ChildStepProps>

  totalNumberOfChildSteps?: number
  numberOfCompletedChildSteps?: number
}

export function Step(props: StepProps) {
  const [isExtended, setIsExtended] = useState(false)
  const { t } = useTranslation()
  const theme = useTheme()
  const isTooSmall = useIsMobile()

  useEffect(() => {
    // if there are still some tasks not done, auto extend
    if (props.numberOfCompletedChildSteps !== props.totalNumberOfChildSteps) {
      setIsExtended(true)
    }
  }, [props.numberOfCompletedChildSteps, props.totalNumberOfChildSteps])

  return (
    <Card variant="outlined" sx={{ marginBottom: 2, elevation: 3 }} key={props.title + '-' + props.totalNumberOfChildSteps}>
      <CardHeader
        avatar={isTooSmall ? undefined : <Avatar sx={{ backgroundColor: theme.palette.primary.main, color: 'white' }}>{props.icon}</Avatar>}
        title={<Typography variant={isTooSmall ? "h6" : "h5"}>{t(`page.onboarding.steps.${props.title}.title`)}</Typography>}
        action={<>
          <Stack direction="row" alignItems={"center"}>
            <Typography variant="body1">
              {t('page.onboarding.step.progress', {
                done: props.numberOfCompletedChildSteps,
                total: props.totalNumberOfChildSteps
              })}
            </Typography>
            <IconButton onClick={() => setIsExtended(!isExtended)}>{isExtended ? <KeyboardArrowUp fontSize='large' /> : <KeyboardArrowDown fontSize='large' />}</IconButton>
          </Stack>
        </>}
      />

      <Divider />

      <Box sx={{ padding: 2 }}>
        <Typography variant="body1">{t(`page.onboarding.steps.${props.description}.description`)}</Typography>

        {isExtended && (
          <>
            <Stack direction="column">
              {props.childSteps.map((childStep, index) => (
                <ChildStep key={props.title + '-' + index} {...childStep} mainKey={props.title} />
              ))}
            </Stack>
          </>
        )}
      </Box>
    </Card>
  )
}